/**
 * Quill Editor Component
 * 
 * Wrapper around Quill rich text editor with custom toolbar and styling
 */

import { useRef, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';

/**
 * Quill Editor Component
 * @param {Object} props
 * @param {string} props.value - Editor content (HTML)
 * @param {Function} props.onChange - Callback when content changes (value) => void
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.readOnly - Whether editor is read-only
 * @param {number} props.minHeight - Minimum height in pixels
 */
export default function QuillEditor({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  readOnly = false,
  minHeight = 300,
}) {
  const quillRef = useRef(null);

  // Custom toolbar configuration
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  // Formats allowed in the editor
  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'link',
    'image',
  ];

  // Handle image insertion
  function imageHandler() {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Temporarily disable admin focus check during file dialog
    window.__adminFocusCheckDisabled = true;

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    
    // Handle file selection
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        // No file selected, re-enable after delay
        setTimeout(() => {
          window.__adminFocusCheckDisabled = false;
        }, 300);
        return;
      }

      try {
        // Create a data URL for the image
        const reader = new FileReader();
        reader.onload = (e) => {
          const url = e.target?.result;
          if (url && quill) {
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, 'image', url, 'user');
            quill.setSelection(range.index + 1);
          }
          // Re-enable after image is inserted
          setTimeout(() => {
            window.__adminFocusCheckDisabled = false;
          }, 300);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error reading image file:', error);
        // Re-enable on error
        setTimeout(() => {
          window.__adminFocusCheckDisabled = false;
        }, 300);
      }
    };

    // Handle cancel/close of file dialog
    input.onblur = () => {
      setTimeout(() => {
        window.__adminFocusCheckDisabled = false;
      }, 500);
    };

    input.click();
  }

  // Sanitize content before passing to onChange
  const handleChange = (content) => {
    // Sanitize HTML to prevent XSS
    const sanitized = DOMPurify.sanitize(content, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'img'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
    });
    onChange(sanitized);
  };

  return (
    <div className="quill-editor-wrapper">
      <style>{`
        .quill-editor-wrapper .quill {
          background: white;
          border-radius: 0.5rem;
          border: 1px solid #e5e7eb;
        }
        .quill-editor-wrapper .ql-container {
          min-height: ${minHeight}px;
          font-size: 1rem;
          font-family: inherit;
        }
        .quill-editor-wrapper .ql-editor {
          min-height: ${minHeight}px;
          padding: 1rem;
        }
        .quill-editor-wrapper .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        .quill-editor-wrapper .ql-toolbar {
          border-top-left-radius: 0.5rem;
          border-top-right-radius: 0.5rem;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        .quill-editor-wrapper .ql-toolbar .ql-stroke {
          stroke: #374151;
        }
        .quill-editor-wrapper .ql-toolbar .ql-fill {
          fill: #374151;
        }
        .quill-editor-wrapper .ql-toolbar button:hover,
        .quill-editor-wrapper .ql-toolbar button.ql-active {
          color: #dc2626;
        }
        .quill-editor-wrapper .ql-toolbar button:hover .ql-stroke,
        .quill-editor-wrapper .ql-toolbar button.ql-active .ql-stroke {
          stroke: #dc2626;
        }
        .quill-editor-wrapper .ql-toolbar button:hover .ql-fill,
        .quill-editor-wrapper .ql-toolbar button.ql-active .ql-fill {
          fill: #dc2626;
        }
        .quill-editor-wrapper .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }
        .quill-editor-wrapper .ql-editor a {
          color: #dc2626;
          text-decoration: underline;
        }
        .quill-editor-wrapper .ql-editor h1 {
          font-size: 2rem;
          font-weight: bold;
          margin: 1rem 0;
        }
        .quill-editor-wrapper .ql-editor h2 {
          font-size: 1.5rem;
          font-weight: bold;
          margin: 0.75rem 0;
        }
        .quill-editor-wrapper .ql-editor h3 {
          font-size: 1.25rem;
          font-weight: bold;
          margin: 0.5rem 0;
        }
        .quill-editor-wrapper .ql-editor ul,
        .quill-editor-wrapper .ql-editor ol {
          margin: 0.5rem 0;
          padding-left: 2rem;
        }
        .quill-editor-wrapper .ql-editor li {
          margin: 0.25rem 0;
        }
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
      />
    </div>
  );
}
